/**
 * PostgREST answers at most `DB_MAX_ROWS` rows per request and trims the rest
 * without saying so: a team's project list read in one request silently stops
 * at the thousandth. Reads that grow with a workspace page through it.
 */

/** PostgREST's `db-max-rows` (`supabase/config.toml` `max_rows`, the same in production). */
export const DB_MAX_ROWS = 1000

type PageResult<T, E> = PromiseLike<{ data: T[] | null; error: E | null }>

/** The query methods a keyset walk applies; `build` returns the query with its filters and nothing else. */
interface KeysetQuery<T, E> extends PageResult<T, E> {
  order(column: string, options: { ascending: boolean }): KeysetQuery<T, E>
  limit(count: number): KeysetQuery<T, E>
  gt(column: string, value: string): KeysetQuery<T, E>
}

export interface ReadAllPagesOptions {
  /** A unique, non-null column the walk pages by (`id`, or `project_id` for one user's memberships). */
  key: string
  /** Hand the rows back newest first on this column, as `order(column, desc)` would: nulls first, ties by `key`. */
  newestFirst?: string
}

/**
 * Every row of a query that may pass the cap. Pages by `key` (each page
 * starts after the last key of the one before), so a row inserted or deleted
 * mid-walk can neither repeat a row nor push one out, as an offset would.
 * Returns the first error as the query would have, so callers keep their own
 * error handling and fallbacks.
 */
export async function readAllPages<T, E>(
  build: () => KeysetQuery<T, E>,
  options: ReadAllPagesOptions,
): Promise<{ data: T[]; error: E | null }> {
  const { key, newestFirst } = options
  const rows: T[] = []
  let after: string | null = null
  for (;;) {
    let query = build().order(key, { ascending: true }).limit(DB_MAX_ROWS)
    if (after !== null) query = query.gt(key, after)
    const { data, error } = await query
    if (error) return { data: rows, error }
    const page = data ?? []
    rows.push(...page)
    if (page.length < DB_MAX_ROWS) break
    after = String((page[page.length - 1] as Record<string, unknown>)[key])
  }
  return { data: newestFirst ? sortNewestFirst(rows, newestFirst, key) : rows, error: null }
}

/** Descending on `column` with nulls first (Postgres's default for DESC), ties descending on `key`. */
export function sortNewestFirst<T>(rows: T[], column: string, key: string): T[] {
  const value = (row: T, name: string) => (row as Record<string, unknown>)[name] as string | null | undefined
  return [...rows].sort((a, b) => {
    const left = value(a, column) ?? null
    const right = value(b, column) ?? null
    if (left !== right) {
      if (left === null) return -1
      if (right === null) return 1
      // timestamptz renders in one offset, so the text sorts as the instant does.
      return left < right ? 1 : -1
    }
    const [ka, kb] = [String(value(a, key)), String(value(b, key))]
    return ka < kb ? 1 : ka > kb ? -1 : 0
  })
}
